import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Mail, Phone, CheckCircle2, Archive, ChevronDown, ChevronRight } from "lucide-react";

/**
 * Admin — Contact submissions inbox.
 *
 * Reads /api/admin/contact-submissions, groups by status, lets the owner
 * mark a submission as read / replied / archived. Reply is a mailto: link
 * so the owner's real email client handles the actual response.
 */
const STATUS_ORDER = ["new", "read", "replied", "archived"];
const STATUS_LABEL = {
  new: "New",
  read: "Read",
  replied: "Replied",
  archived: "Archived",
};

function StatusPill({ status }) {
  const map = {
    new: "bg-oxblood text-bone",
    read: "bg-charcoal text-bone",
    replied: "bg-emerald-700 text-bone",
    archived: "bg-stone text-bone",
  };
  return (
    <span className={`inline-block text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full ${map[status] || "bg-stone text-bone"}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
}

function fmtWhen(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function AdminContactSubmissions({ onCountChange }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState("new"); // "new" | "read" | "replied" | "archived" | "all"

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/contact-submissions");
      setItems(data?.items || []);
      if (onCountChange) onCountChange(data?.unread_count || 0);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load submissions."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/contact-submissions/${id}`, { status });
      setItems((prev) => prev.map((it) => (it.id === id ? { ...it, status } : it)));
      const nextUnread = items.reduce((n, it) => n + (it.id === id ? (status === "new" ? 1 : 0) : (it.status === "new" ? 1 : 0)), 0);
      if (onCountChange) onCountChange(nextUnread);
      toast.success(`Marked ${STATUS_LABEL[status].toLowerCase()}.`);
    } catch (e) {
      toast.error(formatApiError(e, "Update failed."));
    }
  };

  const filtered = filter === "all" ? items : items.filter((it) => it.status === filter);
  const counts = STATUS_ORDER.reduce((acc, s) => {
    acc[s] = items.filter((it) => it.status === s).length;
    return acc;
  }, {});

  return (
    <div className="space-y-6" data-testid="admin-contact-submissions">
      <div className="flex flex-wrap items-center gap-3">
        <p className="overline text-charcoal/70 mr-2">Filter</p>
        {[
          { k: "new", label: `New · ${counts.new || 0}` },
          { k: "read", label: `Read · ${counts.read || 0}` },
          { k: "replied", label: `Replied · ${counts.replied || 0}` },
          { k: "archived", label: `Archived · ${counts.archived || 0}` },
          { k: "all", label: `All · ${items.length}` },
        ].map(({ k, label }) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`text-[11px] font-mono uppercase tracking-widest px-3 py-1.5 rounded-full border transition-colors ${
              filter === k
                ? "bg-charcoal text-bone border-charcoal"
                : "bg-white text-charcoal border-borderSubtle hover:border-charcoal"
            }`}
            data-testid={`contact-filter-${k}`}
          >
            {label}
          </button>
        ))}
        <button
          onClick={load}
          className="ml-auto text-[11px] font-mono uppercase tracking-widest text-charcoal/70 hover:text-charcoal underline"
          data-testid="contact-refresh"
        >
          Refresh
        </button>
      </div>

      {loading ? (
        <p className="text-sm text-stone">Loading…</p>
      ) : filtered.length === 0 ? (
        <div className="border border-borderSubtle rounded-md py-16 text-center">
          <p className="overline text-stone mb-2">No submissions</p>
          <p className="text-sm text-charcoal/70">Nothing to show in this view.</p>
        </div>
      ) : (
        <div className="border border-borderSubtle rounded-md divide-y divide-borderSubtle bg-white">
          {filtered.map((it) => {
            const isOpen = expandedId === it.id;
            return (
              <div key={it.id} data-testid={`contact-row-${it.id}`}>
                <button
                  type="button"
                  onClick={() => {
                    setExpandedId(isOpen ? null : it.id);
                    if (!isOpen && it.status === "new") setStatus(it.id, "read");
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-borderSubtle/40 transition-colors"
                  data-testid={`contact-row-toggle-${it.id}`}
                >
                  <span className="shrink-0">
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </span>
                  <StatusPill status={it.status} />
                  <span className="font-serif text-[16px] text-charcoal min-w-0 truncate">
                    {it.first_name} {it.last_name}
                  </span>
                  <span className="text-[13px] text-stone truncate hidden md:inline min-w-0 flex-1">
                    {it.message.slice(0, 90)}{it.message.length > 90 ? "…" : ""}
                  </span>
                  <span className="ml-auto text-[11px] font-mono uppercase tracking-widest text-stone shrink-0">
                    {fmtWhen(it.created_at)}
                  </span>
                </button>
                {isOpen && (
                  <div className="px-6 py-5 bg-borderSubtle/20 space-y-4" data-testid={`contact-row-body-${it.id}`}>
                    <div className="flex flex-wrap gap-x-6 gap-y-2 text-[13px]">
                      <a href={`mailto:${it.email}?subject=${encodeURIComponent(`Re: your message to Bulk Buy Kits`)}`} className="inline-flex items-center gap-1.5 text-oxblood hover:underline" data-testid={`contact-mailto-${it.id}`}>
                        <Mail size={14} strokeWidth={1.5}/> {it.email}
                      </a>
                      {it.phone && (
                        <a href={`sms:${it.phone.replace(/[^\d+]/g, "")}`} className="inline-flex items-center gap-1.5 text-charcoal hover:text-oxblood" data-testid={`contact-sms-${it.id}`}>
                          <Phone size={14} strokeWidth={1.5}/> {it.phone}
                        </a>
                      )}
                    </div>
                    <div className="bg-white border border-borderSubtle rounded-md p-4 whitespace-pre-wrap text-[14px] leading-relaxed text-charcoal">
                      {it.message}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setStatus(it.id, "replied")}
                        disabled={it.status === "replied"}
                        className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest bg-emerald-700 hover:bg-emerald-800 text-bone px-3 py-1.5 rounded-full disabled:opacity-50"
                        data-testid={`contact-mark-replied-${it.id}`}
                      >
                        <CheckCircle2 size={13} strokeWidth={1.8}/> Mark Replied
                      </button>
                      {it.status !== "archived" ? (
                        <button
                          onClick={() => setStatus(it.id, "archived")}
                          className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest bg-stone hover:bg-charcoal text-bone px-3 py-1.5 rounded-full"
                          data-testid={`contact-archive-${it.id}`}
                        >
                          <Archive size={13} strokeWidth={1.8}/> Archive
                        </button>
                      ) : (
                        <button
                          onClick={() => setStatus(it.id, "read")}
                          className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest border border-borderSubtle hover:border-charcoal text-charcoal px-3 py-1.5 rounded-full"
                          data-testid={`contact-unarchive-${it.id}`}
                        >
                          Unarchive
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
